import {
  dateFormat,
  OrganizationDetailFragment,
  UpdateOrganizationMutation,
  UpdateOrganizationMutationVariables,
  useMutationNotifier,
  useUpdateOrganizationMutation,
  weekdaysFromDate,
} from '@alice-financial/alice-sdk'
import { GqlApiError, useMutationOptionsWithInvalidation } from '@alice-financial/api'
import { Button, IconButton, TextField, Tooltip } from '@alice-financial/pretext-ui'
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite'
import * as React from 'react'
import { useProvisionalOrgQuery } from '../../organization/onboarding/useQueryProvisionalOrg'
import { useOrg } from '../organization/useOrg'

type UpdateOrganizationApiError = GqlApiError<UpdateOrganizationMutationVariables>

interface LaunchDateInputProps {
  org: OrganizationDetailFragment
}

export const LaunchDateInput = ({ org }: LaunchDateInputProps) => {
  const [launchDateInputValue, setLaunchDateInputValue] = React.useState(org.launchDate)
  const mutationOptions = useMutationOptionsWithInvalidation(
    useMutationNotifier<
      UpdateOrganizationMutation,
      UpdateOrganizationApiError,
      UpdateOrganizationMutationVariables
    >(),
    [useProvisionalOrgQuery.getKey(), useOrg.getKey({ orgId: org.id })]
  )
  const { mutate: setLaunchDate, isLoading: isSettingLaunchDate } =
    useUpdateOrganizationMutation(mutationOptions)

  // Determine if the date input should be displayed
  const isOnboarded = Boolean(org.isOnboarded)
  const showDateInput = !isOnboarded
  if (!showDateInput) return null

  const handleChange = ({ target: { value } }: React.FocusEvent<HTMLInputElement>) =>
    setLaunchDateInputValue(value)

  const onSubmit = () => setLaunchDate({ input: { orgId: org.id, launchDate: launchDateInputValue } })

  const isPayrollConnected = isOnboarded || Boolean(org.payrollConnection.payrollConnectedAt)

  const defaultDate = launchDateInputValue || ''
  const minDate = isPayrollConnected ? new Date() : weekdaysFromDate(new Date(), 3)

  // Only render if the date input should be displayed

  const maySubmit = !isSettingLaunchDate && launchDateInputValue && launchDateInputValue !== org.launchDate

  return (
    <>
      <TextField
        type="date"
        size="small"
        value={defaultDate}
        disabled={isSettingLaunchDate}
        slotProps={{
          htmlInput: { min: dateFormat.isoDate(minDate) },
        }}
        onChange={handleChange}
        sx={{
          display: 'block',
          margin: 0,
          width: '150px',
          overflow: 'visible',
          '& .MuiInputBase-root': {
            fontSize: '0.875rem',
          },
          '& .MuiInputBase-input': {
            padding: '6px 8px',
          },
        }}
      />

      {maySubmit ? (
        <Button size="small" variant="contained" onClick={onSubmit} sx={{ ml: 1, mr: -1 }}>
          {org.launchDate ? 'Update' : 'Save'}
        </Button>
      ) : (
        <Tooltip title="Set launch date" arrow>
          <IconButton color="primary" sx={{ ml: 1, mr: -1 }} disabled>
            <PlayCircleFilledWhiteIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  )
}
